import React from "react";
import PropTypes from "prop-types";
import { useLocation, useNavigate } from "react-router-dom";
import "./PageHeader.css";

// Utility to get breadcrumb trail from path and state
const getBreadcrumbs = (pathname, state) => {
  // Dynamic mapping for category keys to display names
  const categoryMap = {
    "life": "Life",
    "retail": "Retail",
    "retail-life": "Retail Life",
    "retail-pension": "Pension",
    "corporate": "Corporate",
    "pension": "Pension",
    // Add more as needed
  };
  const map = {
    "dashboard": "Dashboard",
    "insurance": "Products",
    "products": "Products",
    ...categoryMap,
    "settings": "Settings",
    "profile": "Profile",
    "quotes": "Quotes",
    "contracts": "Contracts",
    "companies": "Companies",
    "audit-log": "Audit Log",
    "schedule-upload": "Schedule Upload",
    // ...add more as needed
  };
  const segments = pathname.split("/").filter(Boolean);

  // --- Custom Transactions History Breadcrumbs ---
  // For /dashboard/transactions-history, mimic product-details: Transactions > [category] > [product name]
  if (segments[0] === "dashboard" && segments[1] === "transactions-history") {
    let crumbs = ["Transactions"];
    // If category in state
    let category = state?.category;
    if (category) {
      crumbs.push(categoryMap[category] || (category.replace(/-/g, " ").replace(/\b\w/g, c => c.toUpperCase())));
    }
    // If product name in state
    let productName = state?.productDetails?.policyName || state?.details?.name;
    if (productName) {
      crumbs.push(productName);
    }
    return crumbs.length > 1 ? crumbs : ["Transactions History"];
  }

  // --- Transaction History Pages Breadcrumbs ---
  // For /dashboard/pension/transactions, /dashboard/life/transactions, /dashboard/corporate/transactions routes
  // Also support /pension/transactions, /life/transactions, /corporate/transactions routes
  if ((segments.length === 3 && segments[0] === 'dashboard' && ['pension', 'life', 'corporate'].includes(segments[1]) && segments[2] === 'transactions') ||
      (segments.length === 2 && ['pension', 'life', 'corporate'].includes(segments[0]) && segments[1] === 'transactions')) {
    const category = segments[0] === 'dashboard' ? segments[1] : segments[0];
    const categoryDisplay = categoryMap[category] || (category.replace(/-/g, " ").replace(/\b\w/g, c => c.toUpperCase()));
    let crumbs = ["Transactions", categoryDisplay];
    
    return crumbs;
  }

  // --- Unified Dynamic Transactions Breadcrumbs ---
  // For any /dashboard/transactions* route, build breadcrumbs: Transactions > [Category] > [Product] > Transactions
  if (segments[0] === "dashboard" && segments[1] === "transactions") {
    let crumbs = ["Transactions"];
    // If category in path (e.g. /dashboard/transactions/life)
    let category = state?.category;
    if (segments[2] && !["product", "dashboard"].includes(segments[2])) {
      category = segments[2];
    }
    if (category) {
      crumbs.push(categoryMap[category] || (category.replace(/-/g, " ").replace(/\b\w/g, c => c.toUpperCase())));
    }
    // If product in path or state
    let productName = state?.productDetails?.policyName || state?.details?.name;
    if (segments[3] === "product" && productName) {
      crumbs.push(productName);
    }
    // Always end with 'Transactions' for product-level
    if (segments[3] === "product") {
      crumbs.push("Transactions");
    }
    return crumbs;
  }

  // --- Specific Transaction History Breadcrumbs ---
  // For /dashboard/life/transactions-history, /dashboard/corporate/transactions-history, /dashboard/pension/transactions-history routes
  // Also support /life/transactions-history, /corporate/transactions-history, /pension/transactions-history routes
  if ((segments.length === 3 && segments[0] === 'dashboard' && ['pension', 'life', 'corporate'].includes(segments[1]) && segments[2] === 'transactions-history') ||
      (segments.length === 2 && ['pension', 'life', 'corporate'].includes(segments[0]) && segments[1] === 'transactions-history')) {
    const category = segments[0] === 'dashboard' ? segments[1] : segments[0];
    const categoryDisplay = categoryMap[category] || (category.replace(/-/g, " ").replace(/\b\w/g, c => c.toUpperCase()));
    let crumbs = ["Transactions", categoryDisplay];
    
    let productName = state?.productDetails?.policyName || state?.details?.name;
    if (productName) {
      crumbs.push(productName);
    }
    
    return crumbs;
  }

  // --- Product Details Breadcrumbs (existing logic) ---
  // Support both '/product-details' and '<category>-product-details' routes
  const productDetailsSegment = segments.find(s => s === 'product-details' || s.endsWith('-product-details'));
  if (productDetailsSegment && state?.productDetails?.policyName) {
    // Determine category: prefer explicit state.category, otherwise derive from the segment prefix
    let category = state?.category;
    if (!category && productDetailsSegment !== 'product-details') {
      // e.g. 'life-product-details' -> 'life'
      category = productDetailsSegment.replace(/-product-details$/, '');
    }
    const categoryDisplay = category ? (categoryMap[category] || (category.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()))) : 'Product';
    return ['Products', categoryDisplay, state.productDetails.policyName];
  }
  let crumbs = [];
  // Always start with Products if on insurance/products or any known category, but not for /dashboard/[category]/transactions
  if (
    segments.includes("insurance") ||
    segments.includes("products") ||
    (Object.keys(categoryMap).some(cat => segments.includes(cat)) && !(segments[0] === "dashboard" && segments[2] === "transactions"))
  ) {
    crumbs.push("Products");
    for (const catKey of Object.keys(categoryMap)) {
      if (segments.includes(catKey)) {
        crumbs.push(categoryMap[catKey]);
        break;
      }
    }
  } else {
    // Fallback: use mapped or capitalized segments
    crumbs = segments.map(seg => map[seg] || seg.replace(/-/g, " ").replace(/\b\w/g, c => c.toUpperCase()));
    // Special case: if Pension is the only crumb, force Products > Pension, unless it's a transactions route
    if (crumbs.length === 1 && crumbs[0] === "Pension" && !(segments[0] === "dashboard" && (segments[1] === "transactions" || segments[2] === "transactions"))) {
      crumbs = ["Products", "Pension"];
    }
  }
  // If on investment-details and state has productDetails, append product name ONLY if not already present
  if (
    segments.includes("product-details") &&
    state?.productDetails?.policyName &&
    (!crumbs.length || crumbs[crumbs.length - 1] !== state.productDetails.policyName)
  ) {
    crumbs.push(state.productDetails.policyName);
  }
  return crumbs;
};

const PageHeader = ({ className = "" }) => {
  const location = useLocation();
  const navigate = useNavigate();
  if (location.pathname === "/dashboard" || location.pathname === "/dashboard/") return null;
  const crumbs = getBreadcrumbs(location.pathname, location.state);
  // Only show breadcrumbs for Products and its subpages (including Pension)
  const isProducts = crumbs[0] === "Products" || (crumbs.length === 2 && crumbs[1] === "Pension");
  // Helper to get category route
  const getCategoryRoute = (category) => {
    // Normalize category to match route
    if (!category) return "/dashboard/products";
    const key = category.toLowerCase().replace(/ /g, "-");
  if (key === "life") return "/dashboard/life";
  if (key === "corporate") return "/dashboard/corporate";
  if (key === "pension") return "/dashboard/pension";
  if (key === "pension" || key === "pensions") return "/dashboard/pension";
    // fallback
    return `/dashboard/${key}`;
  };
  // Helper for transaction category route
  const getTransactionCategoryRoute = (category) => {
    if (!category) return "/dashboard/transactions";
    const key = category.toLowerCase().replace(/ /g, "-");
    if (key === "life") return "/dashboard/transactions/life";
    if (key === "corporate") return "/dashboard/transactions/corporate";
    if (key === "pension" || key === "pensions") return "/dashboard/transactions/pension";
    // fallback
    return `/dashboard/transactions/${key}`;
  };

  if (isProducts || (crumbs[0] === "Transactions")) {
    return (
      <div className={`page-header-wrapper w-100 ${className}`.trim()} style={{ minWidth: 0 }}>
        <div className="page-header d-flex align-items-center justify-content-start w-100" style={{ padding: '20px 0', background: "#fff", borderBottom: "1px solid #eee" }}>
          <span className="page-title fw-bold page-header-breadcrumbs" style={{ fontSize: 20, paddingLeft: 20 }}>
            {crumbs.map((crumb, idx) => {
              const isFirst = idx === 0;
              const isLast = idx === crumbs.length - 1;
              // Products link
              if (isFirst && crumb === "Products") {
                return (
                  <span key={idx} className="breadcrumb-crumb" style={{ display: 'inline-flex', alignItems: 'center' }}>
                    <span className="breadcrumb-link" style={{ cursor: 'pointer', color: '#00704A' }} onClick={() => navigate('/dashboard/products')}>{crumb}</span>
                    {!isLast && <span className="breadcrumb-arrow" style={{ color: '#bbb', margin: '0 8px' }}>{'>'}</span>}
                  </span>
                );
              }
              // Transactions link
              if (isFirst && crumb === "Transactions") {
                return (
                  <span key={idx} className="breadcrumb-crumb" style={{ display: 'inline-flex', alignItems: 'center' }}>
                    <span className="breadcrumb-link" style={{ cursor: 'pointer', color: '#00704A' }} onClick={() => navigate('/dashboard/transactions')}>{crumb}</span>
                    {!isLast && <span className="breadcrumb-arrow" style={{ color: '#bbb', margin: '0 8px' }}>{'>'}</span>}
                  </span>
                );
              }
              // Category link (second crumb, if present)
              if (idx === 1 && crumbs[0] === "Transactions" && crumbs.length > 1) {
                return (
                  <span key={idx} className="breadcrumb-crumb" style={{ display: 'inline-flex', alignItems: 'center' }}>
                    <span className="breadcrumb-link" style={{ cursor: 'pointer', color: '#00704A' }} onClick={() => navigate(getTransactionCategoryRoute(crumb))}>{crumb}</span>
                    {!isLast && <span className="breadcrumb-arrow" style={{ color: '#bbb', margin: '0 8px' }}>{'>'}</span>}
                  </span>
                );
              }
              // Category link for Products (existing)
              if (idx === 1 && crumbs[0] === "Products" && crumbs.length > 1) {
                return (
                  <span key={idx} className="breadcrumb-crumb" style={{ display: 'inline-flex', alignItems: 'center' }}>
                    <span className="breadcrumb-link" style={{ cursor: 'pointer', color: '#00704A' }} onClick={() => navigate(getCategoryRoute(crumb))}>{crumb}</span>
                    {!isLast && <span className="breadcrumb-arrow" style={{ color: '#bbb', margin: '0 8px' }}>{'>'}</span>}
                  </span>
                );
              }
              // All other crumbs (including last)
              return (
                <span key={idx} className="breadcrumb-crumb" style={{ display: 'inline-flex', alignItems: 'center' }}>
                  {crumb}
                  {!isLast && <span className="breadcrumb-arrow" style={{ color: '#bbb', margin: '0 8px' }}>{'>'}</span>}
                </span>
              );
            })}
          </span>
        </div>
        {/* Responsive breadcrumb styles */}
        <style>{`
          .page-header {
            padding-top: 20px;
            padding-bottom: 20px;
          }
          .page-header-breadcrumbs {
            font-size: 20px;
          }
          .breadcrumb-arrow {
            font-size: 20px;
            transition: font-size 0.18s;
          }
          .breadcrumb-link {
            font-size: 20px;
            transition: font-size 0.18s;
          }
          .breadcrumb-crumb {
            font-size: 20px;
            transition: font-size 0.18s;
          }
          @media (max-width: 900px) {
            .page-header-breadcrumbs,
            .breadcrumb-link,
            .breadcrumb-crumb,
            .breadcrumb-arrow {
              font-size: 17px;
            }
          }
          @media (max-width: 700px) {
            .page-header { padding-top: 10px; padding-bottom: 10px; }
            .page-header-breadcrumbs,
            .breadcrumb-link,
            .breadcrumb-crumb,
            .breadcrumb-arrow {
              font-size: 15px;
            }
          }
          @media (max-width: 480px) {
            .page-header { padding-top: 8px; padding-bottom: 8px; }
            .page-header-breadcrumbs,
            .breadcrumb-link,
            .breadcrumb-crumb,
            .breadcrumb-arrow {
              font-size: 12px;
            }
          }
        `}</style>
      </div>
    );
  } else {
    // For all other pages, just show the page name (last crumb)
    return (
      <div className={`page-header-wrapper w-100 ${className}`.trim()} style={{ minWidth: 0 }}>
        <div className="page-header d-flex align-items-center justify-content-start w-100" style={{ padding: '20px 0', background: "#fff", borderBottom: "1px solid #eee" }}>
          <span className="page-title fw-bold page-header-breadcrumbs" style={{ fontSize: 20, paddingLeft: 20 }}>{crumbs[crumbs.length - 1]}</span>
        </div>
        <style>{`
          .page-header { padding-top: 20px; padding-bottom: 20px; }
          .page-header-breadcrumbs { font-size: 20px; }
          @media (max-width: 900px) {
            .page-header-breadcrumbs { font-size: 17px; }
            .page-header { padding-top: 10px; padding-bottom: 10px; }
          }
          @media (max-width: 700px) {
            .page-header-breadcrumbs { font-size: 15px; }
            .page-header { padding-top: 8px; padding-bottom: 8px; }
          }
          @media (max-width: 480px) {
            .page-header-breadcrumbs { font-size: 12px; }
            .page-header { padding-top: 6px; padding-bottom: 6px; }
          }
        `}</style>
      </div>
    );
  }
};

PageHeader.propTypes = {
  className: PropTypes.string,
};

export default PageHeader;
